import { BootstrapUtility, Logger, TsConfig } from './utility/VizclientUtils';
import { BrowserEventName } from '../gen-src/preslayer/Enums';
import { BrowserSupport } from '@tableau/browser-support';
import { IMouseCaptureEventHandler } from './definition/IMouseCaptureEventHandler';
import { MiscUtil } from './utility/MiscUtil';
import { Param } from './utility/Param';

/**
* A utility class that uses event capture to detect the mouse leaving the document.
*/
class MouseCapture {
  private static eventHandlers: IMouseCaptureEventHandler[] = [];
  private static areMouseListenersAttached: boolean = false;

  /**
  * returns true if we can use event listeners on the document in order to watch mouse events
  * during the capture phase.
  */
  private static get ManuallyCaptureMouseEvents(): boolean {
    // Not on mobile - capture is done natively on mobile, so we don't want to override that implementation,
    // and only if browser supports AddEventListener on the Document (IE 7/8 does not)
    return !TsConfig.IsMobile() && BrowserSupport.AddEventListener();
  }

  /**
  * Tells us whether it's ok to force a drag stop event when the mouse leaves the document and we're in
  * an iFrame.  This prevents the situation where mouse is released outside the document and when mouse reenters
  * the document, we're still dragging even though the mouse is up.
  */
  private static get ForceDragStopOnIFrameMouseOut(): boolean {
    // We only need to force a stop drag on mouse leaving the window in IE - other browsers work fine without it,
    // and only if we're in an iFrame
    return BrowserSupport.IsIE() && BootstrapUtility.IsEmbedded();
  }

  /**
  * Normal case is to call this in response to a mousedown event where dragging/tracking will occur until mouseup.
  *
  * Both IE and Firefox have built-in support for setCapture.
  * https://developer.mozilla.org/en-US/docs/Web/API/Element.setCapture
  *
  * Chrome(WebKit) doesn't have setCapture so we're going to use the mouse capture phase
  * to steal any mousemove/up/out events so that we can be sure no one is stopping them.
  * When we can't use setCapture and we are in an iFrame, it's not possible to receive mouse events once
  * the mouse has left the document, so in this case we complete the capture as soon as the mouse leaves the document.
  *
  * @param {IMouseCaptureEventHandler} handler - The event handler used to receive events in the case we handle mouse
  * events manually.
  */
  public static SetCapture(handler: IMouseCaptureEventHandler): void {
    Param.VerifyValue(handler, 'handler');
    if (MouseCapture.ManuallyCaptureMouseEvents) {
      MouseCapture.eventHandlers.push(handler);

      if (!MouseCapture.areMouseListenersAttached) {
        // listen for the mouse events during the capture phase (pass true for useCapture)
        document.addEventListener(BrowserEventName.MouseMove, this.HandleMouseMove, true);
        document.addEventListener(BrowserEventName.MouseUp, this.HandleMouseUp, true);
        document.addEventListener(BrowserEventName.MouseOut, this.HandleMouseOut, true);

        MouseCapture.areMouseListenersAttached = true;
      }
    }
  }

  /**
  * Ends mouse event capture for all previously set handlers.
  */
  public static ReleaseCapture(): void {
    if (MouseCapture.ManuallyCaptureMouseEvents) {
      MouseCapture.eventHandlers = [];

      document.removeEventListener(BrowserEventName.MouseMove, this.HandleMouseMove, true);
      document.removeEventListener(BrowserEventName.MouseUp, this.HandleMouseUp, true);
      document.removeEventListener(BrowserEventName.MouseOut, this.HandleMouseOut, true);

      MouseCapture.areMouseListenersAttached = false;
    }
  }

  private static HandleMouseMove(e: Event): void {
    MouseCapture.eventHandlers.forEach(handler => handler.HandleCapturedMouseMove(e as TouchEvent));
  }

  private static HandleMouseUp(e: Event): void {
    MouseCapture.HandleComplete(e);
    Logger.Debug('Mouse has been released, calling HandleCaptureComplete on the handler');
  }

  private static HandleMouseOut(e: Event): void {
    // test to see if the mouse has left the document
    if (MouseCapture.ForceDragStopOnIFrameMouseOut && MouseCapture.EventIsOutsideDocument(e)) {
      MouseCapture.HandleComplete(e);
      Logger.Debug('Mouse is Exiting document and document is in an iFrame - calling HandleCapturedMouseComplete on the handler');
    }
  }

  private static HandleComplete(e: Event): void {
    MouseCapture.eventHandlers.forEach(handler => handler.HandleCapturedMouseComplete(e as TouchEvent));
  }

  private static EventIsOutsideDocument(e: Event): boolean {
    // tslint:disable-next-line:no-any whitespace
    let toElement = (<any>e).toElement;
    // Below from http://stackoverflow.com/questions/923299/how-can-i-detect-when-the-mouse-leaves-the-window
    return (MiscUtil.IsNullOrUndefined(toElement) || toElement.NodeName === 'HTML');
  }
}

export { MouseCapture };
